import type { Assignment } from '../api/generated/Assignment'
import type { Position } from '../api/generated/Position'
import type { Relief } from '../api/generated/Relief'
import type { Shift } from '../api/generated/Shift'

export type AssignedShift = { assignment: Assignment; shift: Shift; position: Position }

/** Each assignment with its shift and position, in the order of `assignments` (by start time from the API); skips those not loaded. */
export function assignedShifts(
  assignments: Assignment[],
  shifts: Shift[],
  positions: Position[],
): AssignedShift[] {
  const shiftById = new Map(shifts.map((shift) => [shift.id, shift]))
  const positionById = new Map(positions.map((position) => [position.id, position]))
  return assignments.flatMap((assignment) => {
    const shift = shiftById.get(assignment.shift_id)
    const position = shift && positionById.get(shift.position_id)
    return shift && position ? [{ assignment, shift, position }] : []
  })
}

const reliefNotices: Record<Relief, string> = {
  none: 'Pas de relève prévue : vous pourrez partir à la fin du créneau.',
  planned:
    "Attendez votre relève : une notification vous préviendra dès qu'elle aura pris le poste.",
  arrived: 'Votre relève est arrivée : vous pourrez partir à la fin de votre créneau.',
}

/** What a volunteer at their position should do at the end of the shift, once checked in. */
export function reliefNotice(assignment: Assignment): string | undefined {
  if (assignment.attendance !== 'checked_in' || !assignment.relief) return undefined
  return reliefNotices[assignment.relief]
}

/**
 * How the volunteer can leave the shift: withdraw themselves (accepted, before the event's
 * deadline), or write to the organizers once the deadline has passed and until the shift starts.
 */
export function withdrawalOption(
  { assignment, shift }: Pick<AssignedShift, 'assignment' | 'shift'>,
  now = new Date(),
): 'withdraw' | 'contact' | null {
  if (assignment.withdrawable) return 'withdraw'
  if (assignment.status !== 'accepted') return null
  return new Date(shift.starts_at) > now ? 'contact' : null
}
